import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import LazyLoad from "react-lazyload";
import { Helmet } from "react-helmet";
import americorps from "../../assets/images/volunteer/americorps.webp";
import construction from "../../assets/images/volunteer/construction.webp";
import leader from "../../assets/images/volunteer/leader.webp";
import nonconstruction from "../../assets/images/volunteer/non-construction.webp";

import restore from "../../assets/images/volunteer/restore.webp";
import Card from "../../components/Card";
import CardSection from "../../components/CardSection";
import Accordion from "../../components/Accordion";
import CTASection from "../../components/CTASection";
import DynamicEvents from "../../components/DynamicEvents";

const GetInvolved = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How do I sign up to volunteer?",
      answer:
        "First, complete our registration and waiver. You will be redirected to our volunteer home page where you can select your volunteer experience.",
    },
    {
      question: "How old do I have to be to volunteer?",
      answer:
        "Volunteers must be 16 or older. Minors are required to have a parent/guardian sign-off on their waiver.",
    },
    {
      question: "Do I need experience to volunteer?",
      answer: "Nope, just a good attitude and a willingness to learn!",
    },
    {
      question: "What time do I volunteer?",
      answer:
        "Volunteer shifts can be found on the volunteer calendar. After registering, you will receive a confirmation email with details about your build day. For questions, please email our Volunteer Coordinator.",
    },
    {
      question: "What should I wear to volunteer?",
      answer:
        "If you are working on a construction site or in the ReStore, dress appropriately for the weather. We suggest layers, waterproof shoes, sunscreen, and sunglasses. Closed-toe shoes are required.",
    },
    {
      question: "Should I bring a lunch?",
      answer:
        "Yes, be sure to bring a lunch. Please note, there is no refrigeration on our construction sites.",
    },
    {
      question: "Should I bring water?",
      answer:
        "We will have water on site, but suggest you bring a refillable water bottle.",
    },
    {
      question: "Should I bring my own tools?",
      answer:
        "All tools, supplies, and safety gear will be provided by Habitat Buffalo. We are not responsible for tools that are lost, stolen, or left behind.",
    },
    {
      question:
        "What is the difference between a “sponsor group” and a “non-sponsor group”?",
      answer:
        "Sponsors make our mission possible by funding all or a portion of the cost of a house. In exchange for their donation, sponsors are entitled to special build days on the house they’ve funded. Non-sponsor groups are teams that have not made a financial contribution.",
    },
    {
      question: "What will I be working on when I volunteer?",
      answer:
        "Tasks at the ReStore and on the construction site vary. We cannot guarantee any specific kind of work since most of the work is completed by volunteers, making it difficult to predict our schedule. Please come prepared to follow the direction of your supervisor.",
    },
    {
      question: "How many people can volunteer on one site per day?",
      answer:
        "Groups can bring as many as ten people per day. If you would like to make arrangements for a larger group, be sure to check out our sponsorship opportunities.",
    },
    {
      question:
        "How can I help Habitat Buffalo if I don’t want to do construction?",
      answer:
        "There are many ways to get involved! We can make use of anyone’s time, talent, and enthusiasm. Lend a hand in our ReStore, or learn about other ways to volunteer!",
    },
    {
      question: "Can I sign up for school community service?",
      answer:
        "Of course! Students, ages 16 and older, may sign up for any available volunteer opportunity. Please email your tracking sheet to our Volunteer Coordinator.",
    },
    {
      question: "Do you accept court-mandated volunteers?",
      answer:
        "Court-mandated volunteers are considered based on the charge(s). Please email our Volunteer Coordinator directly for more information.",
    },
  ];

  const cards = [
    {
      title: "Construction",
      description:
        "Join us in building homes, communities, and hope. Whether you’re picking up a paintbrush or a hammer, your efforts will make a difference in the lives of Buffalo families.",
      link: "/volunteer/construction",
      image: construction,
    },
    {
      title: "ReStores",
      description:
        "Support our mission by volunteering at one of our ReStores. Help us with inventory, customer service, and more. You might even find a great bargain while you're there!",
      link: "/volunteer/restores",
      image: restore,
    },

    {
      title: "Non-Construction",
      description:
        "Support Habitat Buffalo through non-construction opportunities. Assist with events, outreach, office work, and more.",
      link: "/volunteer/non-construction",
      image: nonconstruction,
    },
    {
      title: "Crew Leader Training",
      description:
        "Become a Crew Leader and lead volunteer teams on our construction sites. Training is provided to ensure you have the skills needed to guide and assist volunteers.",
      link: "/volunteer/crew-leader-training",
      image: leader,
    },
    {
      title: "AmeriCorps",
      description:
        "Join AmeriCorps and serve with Habitat Buffalo. Gain valuable experience, build leadership skills, and make a lasting impact in our community.",
      link: "/volunteer/americorps",
      image: americorps,
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Individual Volunteer Opportunities - Habitat for Humanity Buffalo
        </title>
        <meta
          name="description"
          content="Explore individual volunteer opportunities at Habitat for Humanity Buffalo. Help us build homes, assist in ReStores, or volunteer remotely to make a difference."
        />
      </Helmet>
      <Container>
        <Overview>
          <OverviewText>
            <h2 className="section-header orange">
              Individual Volunteer Opportunities
            </h2>
            <p>
              There are many ways to get involved as an individual volunteer.
              Whether you want to work on-site, in our ReStores, or even from
              home, we have opportunities that fit your skills and schedule.
            </p>
          </OverviewText>
        </Overview>
        <CardSection>
          {cards.map((card, index) => (
            <LazyLoad key={index} height={200} offset={100}>
              <Card
                title={card.title}
                description={card.description}
                link={card.link}
                image={card.image}
              />
            </LazyLoad>
          ))}
        </CardSection>
        <CTASection
          title="Sign Up to Volunteer"
          description="Join us in building homes, communities, and hope. Whether you’re picking up a paintbrush or a hammer, your efforts will make a difference in the lives of Buffalo families."
          buttonText="SIGN UP TO VOLUNTEER"
          buttonLink="https://habitatbflo.my.site.com/volunteer/s/sign-up?jId=a0T2J00000nmvrtUAA"
        />
        <DynamicEvents
          category="individual_volunteers"
          categoryText="Individual Volunteer"
        />
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const Overview = styled.div`
  padding: 50px 20px 0 20px;
  text-align: center;
`;

const OverviewText = styled.div`
  font-size: 1.2em;
  max-width: 800px;
  margin: 0 auto;
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const SignUpSection = styled.section`
  background-color: var(--hhb-primary);
  color: white;
  padding: 40px 20px;
  text-align: center;
  border-radius: 8px;
  margin: 40px 0;

  @media (max-width: 768px) {
    padding: 30px 15px;
    margin: 30px 0;
  }

  @media (max-width: 480px) {
    padding: 20px 10px;
    margin: 20px 0;
  }
`;

const SignUpText = styled.p`
  font-size: 1.2em;
  margin: 20px 0;

  @media (max-width: 768px) {
    font-size: 1.1em;
    margin: 15px 0;
  }

  @media (max-width: 480px) {
    font-size: 1em;
    margin: 10px 0;
  }
`;

const ButtonContainer = styled.div`
  text-align: center;
`;

const SignUpButton = styled.a`
  background-color: white;
  color: var(--hhb-primary);
  padding: 15px 30px;
  text-decoration: none;
  border-radius: 5px;
  font-size: 1.2em;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--hhb-darkblue);
    color: white;
  }

  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 1.1em;
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 1em;
  }
`;

const AccordionSection = styled.div`
  margin-top: 40px;

  @media (max-width: 768px) {
    margin-top: 30px;
  }

  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

export default GetInvolved;
